import { useMemo, useRef, useState } from 'react';
import { Check, FileDown } from 'lucide-react';
import SignaturePad from '@/components/SignaturePad';
import PncFooter from '@/components/PncFooter';
import { apiPublic, API_BASE } from '@/lib/api';

const OPTION_A_TEXT =
  'I voluntarily agree that the 48-hour average weekly working time limit set out in the ' +
  'Working Time Regulations 1998 will not apply to me while this agreement remains in force.';
const OPTION_B_TEXT =
  'I do not agree to opt out of the 48-hour average weekly working time limit. I wish to ' +
  'retain the protection of this limit under the Working Time Regulations 1998.';
const CONFIRMATION_TEXT =
  'I confirm that I have read and understood the information provided in this document. ' +
  'I confirm that the option selected above reflects my own voluntary decision.';

function OptionCard({ id, letter, title, text, selected, onSelect }) {
  return (
    <label
      data-testid={`optout-option-${id}`}
      className={`block p-5 rounded-lg border cursor-pointer transition-colors ${
        selected
          ? 'border-[var(--silver)] bg-[#1A1A1A]'
          : 'border-[var(--line)] hover:border-[#4A4A4A]'
      }`}
    >
      <input
        type="radio"
        name="optout_choice"
        value={id}
        checked={selected}
        onChange={() => onSelect(id)}
        className="sr-only"
      />
      <div className="flex items-start gap-3.5">
        <span
          className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 ${
            selected ? 'border-[var(--silver)] bg-[var(--silver)]' : 'border-[#4A4A4A]'
          }`}
        >
          {selected && <Check size={14} strokeWidth={3} className="text-[#0A0A0A]" />}
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-[var(--muted)] mb-1">
            Option {letter}
          </p>
          <p className="font-semibold text-sm mb-2">{title}</p>
          <p className="text-sm text-[#C9C9C9] leading-relaxed">&ldquo;{text}&rdquo;</p>
        </div>
      </div>
    </label>
  );
}

export default function OptOutForm({ token, meta }) {
  const company = meta.contract_type === 'unique' ? 'PNC Unique LTD' : 'PNC Smart Solutions LTD';
  const [choice, setChoice] = useState(null); // never preselected
  const [workerName, setWorkerName] = useState(meta.contractor_name || '');
  const [workerCompany, setWorkerCompany] = useState(meta.contractor_company || '');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const sigRef = useRef(null);

  const todayDisplay = useMemo(() => new Date().toLocaleDateString('en-GB'), []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!choice) { setError('Please select Option A or Option B before continuing.'); return; }
    if (!workerName.trim()) { setError('Please enter your full name.'); return; }
    if (!workerCompany.trim()) { setError('Please enter your company name.'); return; }
    if (!confirmed) { setError('Please tick the confirmation box before submitting.'); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError('Please draw your signature before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiPublic.post(`/contracts/sign/${token}/submit-optout`, {
        choice,
        worker_name: workerName.trim(),
        worker_company: workerCompany.trim(),
        signature_png: sigRef.current.toDataURL(),
        declaration_confirmed: true,
      });
      setDone(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grain flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-16">
          <div data-testid="optout-success" className="max-w-lg w-full pnc-card rounded-lg p-10 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-4">
              <Check size={22} strokeWidth={2.5} />
            </div>
            <h1 className="serif text-3xl mb-3">Declaration recorded</h1>
            <p className="text-sm text-[var(--muted)] mb-2">
              Thank you. Your 48-Hour Working Time Declaration has been successfully submitted and recorded.
            </p>
            <p className="text-sm text-[#D4D4D4] mb-6">
              Your choice:{' '}
              <strong className="text-[var(--text)]">
                {done.optout_choice === 'opt_out'
                  ? 'Option A — agreed to opt out'
                  : 'Option B — did not agree to opt out'}
              </strong>
            </p>
            <a
              data-testid="optout-download-pdf"
              href={`${API_BASE}/contracts/sign/${token}/signed-pdf`}
              target="_blank"
              rel="noreferrer"
              className="btn-ink inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-xs font-bold uppercase tracking-[0.08em]"
            >
              <FileDown size={14} strokeWidth={2} /> Download your signed copy
            </a>
          </div>
        </div>
        <PncFooter />
      </div>
    );
  }

  return (
    <div data-testid="optout-form-page" className="min-h-screen grain">
      <header className="relative border-b border-[var(--line)] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/pnc-logo-white.png" alt="PNC UNIQUE LTD" className="h-9 w-auto" />
          <span className="text-xs text-[var(--muted)]">Secure e-signature</span>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        <p className="field-label mb-2">Voluntary declaration</p>
        <h1 className="serif text-4xl leading-tight mb-1">48-Hour Working Time Declaration &amp; Opt-Out Agreement</h1>
        <p className="text-sm text-[var(--muted)] mb-1">{company}</p>
        <p className="text-sm text-[var(--muted)] mb-8">
          Sent to <strong>{meta.employee_email}</strong>
          {meta.expires_at ? <> &middot; expires {new Date(meta.expires_at).toLocaleDateString()}</> : null}
        </p>

        <form data-testid="optout-form" onSubmit={submit} className="space-y-8">
          {/* Step 1 — read the document */}
          <section className="pnc-card rounded-lg p-7 space-y-4">
            <div>
              <p className="field-label mb-1">Step 1</p>
              <p className="serif text-2xl leading-snug">Read the agreement</p>
              <p className="text-xs text-[var(--muted)] mt-1">
                Please read the full document below, including your rights and the withdrawal procedure.
              </p>
            </div>
            <iframe
              data-testid="optout-document-iframe"
              title="48-Hour Working Time Opt-Out Agreement"
              src={`${API_BASE}/contracts/sign/${token}/document`}
              className="w-full h-[480px] rounded-md border border-[var(--line)] bg-white"
            />
          </section>

          {/* Step 2 — choose an option */}
          <section className="pnc-card rounded-lg p-7 space-y-4">
            <div>
              <p className="field-label mb-1">Step 2</p>
              <p className="serif text-2xl leading-snug">Select one option</p>
              <p className="text-xs text-[var(--muted)] mt-1">
                This decision is entirely voluntary. Both options allow you to sign and submit.
              </p>
            </div>
            <OptionCard
              id="opt_out"
              letter="A"
              title="I AGREE TO OPT OUT"
              text={OPTION_A_TEXT}
              selected={choice === 'opt_out'}
              onSelect={(v) => { setChoice(v); setError(''); }}
            />
            <OptionCard
              id="not_opt_out"
              letter="B"
              title="I DO NOT AGREE TO OPT OUT"
              text={OPTION_B_TEXT}
              selected={choice === 'not_opt_out'}
              onSelect={(v) => { setChoice(v); setError(''); }}
            />
          </section>

          {/* Steps 3 & 4 — details, confirmation, signature */}
          <section className="pnc-card rounded-lg p-7 space-y-5">
            <div>
              <p className="field-label mb-1">Step 3</p>
              <p className="serif text-2xl leading-snug">Your details &amp; signature</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label block mb-1.5">
                  Full name <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="optout-worker-name"
                  value={workerName}
                  onChange={(e) => { setWorkerName(e.target.value); setError(''); }}
                  placeholder="Your full name"
                  className="input-bare"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="field-label block mb-1.5">
                  Company name <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="optout-worker-company"
                  value={workerCompany}
                  onChange={(e) => { setWorkerCompany(e.target.value); setError(''); }}
                  placeholder="Your company"
                  className="input-bare"
                  maxLength={200}
                />
              </div>
            </div>

            <div>
              <label className="field-label block mb-1.5">Date</label>
              <input data-testid="optout-date" value={todayDisplay} readOnly className="input-bare opacity-70 cursor-default" />
            </div>

            <div className="bg-[var(--paper-deep)] border border-[var(--line)] rounded-md p-4">
              <label className="flex items-start gap-2.5 text-sm text-[#D4D4D4] cursor-pointer">
                <input
                  data-testid="optout-confirm-checkbox"
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => { setConfirmed(e.target.checked); setError(''); }}
                  className="accent-[var(--silver)] mt-0.5"
                />
                <span>&ldquo;{CONFIRMATION_TEXT}&rdquo;</span>
              </label>
            </div>

            <div>
              <label className="field-label block mb-1.5">
                Your signature <span className="text-red-500">*</span>
              </label>
              <SignaturePad ref={sigRef} />
            </div>

            {error && (
              <div data-testid="optout-error" className="alert-error text-sm rounded p-3">{error}</div>
            )}

            <button
              data-testid="optout-submit"
              type="submit"
              disabled={submitting || !choice || !confirmed || !workerName.trim() || !workerCompany.trim()}
              className="btn-ink w-full py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.08em] disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit declaration'}
            </button>
          </section>
        </form>
      </main>

      <PncFooter />
    </div>
  );
}
